import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Edit, Plus, Mail, Phone, Trash2, Eye, 
  Shield, TrendingUp, Calendar, MapPin, User, FileText,
  CreditCard, MessageCircle, AlertCircle, Save, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import axios from 'axios';

import { ClientActivityTimeline } from '../components/ClientActivityTimeline';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Perfil del Cliente - Interfaz Optimizada con Acciones Funcionales
 * 
 * Acciones implementadas:
 * 1. Editar Información - Modal con formulario pre-cargado
 * 2. Enviar Email - Abre cliente de correo predeterminado
 * 3. Registrar Llamada - Modal para log de actividad
 * 4. Eliminar Cliente - Modal de confirmación de alto riesgo
 */
export const ClientProfileOptimized = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [client, setClient] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [activities, setActivities] = useState([]);
  const [metrics, setMetrics] = useState({
    totalPolicies: 0,
    activePolicies: 0,
    totalInsuredAmount: 0,
    totalAnnualPremium: 0
  });

  // ========================================
  // ESTADOS PARA MODALES DE ACCIONES
  // ========================================
  
  // Modal de Edición
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    alias: '',
    id_number: '',
    email: '',
    phone: '',
    dob: '',
    current_address: {
      street: '',
      city: '',
      state: '',
      postal_code: '',
      country: 'México'
    }
  });
  const [saving, setSaving] = useState(false);

  // Modal de Registro de Llamada
  const [callLogModalOpen, setCallLogModalOpen] = useState(false);
  const [callLogData, setCallLogData] = useState({
    callDate: new Date().toISOString().split('T')[0],
    duration: '',
    notes: '',
    outcome: 'completed'
  });
  const [savingCallLog, setSavingCallLog] = useState(false);

  // Modal de Eliminación
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (clientId) {
      loadClientData();
    }
  }, [clientId]);

  const loadClientData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(
        `${API_URL}/api/clients-master/${clientId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setClient(response.data.client);
      setPolicies(response.data.policies || []);
      
      // Cargar actividades (Fix 2)
      try {
        const activitiesResponse = await axios.get(
          `${API_URL}/api/clients-master/${clientId}/activities`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setActivities(activitiesResponse.data.activities || []);
      } catch (actError) {
        console.warn('Could not load activities:', actError);
        // No bloquear la carga principal
      }
      
      // Inicializar formulario de edición con datos actuales
      setEditFormData({
        name: response.data.client.name || '',
        alias: response.data.client.alias || '',
        id_number: response.data.client.id_number || '',
        email: response.data.client.email || '',
        phone: response.data.client.phone || '',
        dob: response.data.client.dob || '',
        current_address: response.data.client.current_address || {
          street: '',
          city: '',
          state: '',
          postal_code: '',
          country: 'México'
        }
      });
      
      // Calcular métricas
      const activePolicies = (response.data.policies || []).filter(
        p => ['En Vigor', 'Vigente', 'Activa', 'Active'].includes(p.status)
      );
      
      const totalInsuredAmount = activePolicies.reduce(
        (sum, p) => sum + (p.insured_amount || 0), 0
      );
      
      const totalAnnualPremium = activePolicies.reduce(
        (sum, p) => sum + (p.annual_premium || 0), 0
      );
      
      setMetrics({
        totalPolicies: (response.data.policies || []).length,
        activePolicies: activePolicies.length,
        totalInsuredAmount,
        totalAnnualPremium
      });
      
    } catch (error) {
      console.error('Error loading client:', error);
      toast.error('Error al cargar el cliente');
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // ACCIÓN 1: EDITAR INFORMACIÓN
  // ========================================
  
  const handleOpenEditModal = () => {
    // Recargar datos actuales en el formulario
    setEditFormData({
      name: client.name || '',
      alias: client.alias || '',
      id_number: client.id_number || '',
      email: client.email || '',
      phone: client.phone || '',
      dob: client.dob || '',
      current_address: client.current_address || {
        street: '',
        city: '',
        state: '',
        postal_code: '',
        country: 'México'
      }
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      
      // Fix 1: Sanitizar datos antes de enviar (email vacío -> null)
      const payload = {
        ...editFormData,
        email: editFormData.email || null, // Convertir string vacío a null
        phone: editFormData.phone || null,
        id_number: editFormData.id_number || null,
        dob: editFormData.dob || null
      };

      await axios.put(
        `${API_URL}/api/clients-master/${clientId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Cliente actualizado exitosamente');
      setEditModalOpen(false);
      loadClientData(); // Recargar datos
    } catch (error) {
      console.error('Error updating client:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar cliente');
    } finally {
      setSaving(false);
    }
  };

  // ========================================
  // ACCIÓN 2: ENVIAR EMAIL
  // ========================================
  
  const handleSendEmail = () => {
    if (!client.email) {
      toast.error('El cliente no tiene un email registrado');
      return;
    }
    
    // Abrir cliente de correo predeterminado
    const subject = encodeURIComponent(`Asunto: ${client.name}`);
    const body = encodeURIComponent(`Estimado/a ${client.name},\n\n`);
    window.location.href = `mailto:${client.email}?subject=${subject}&body=${body}`;
    
    toast.success(`Abriendo cliente de correo para: ${client.email}`);
  };

  // ========================================
  // ACCIÓN 3: REGISTRAR LLAMADA
  // ========================================
  
  const handleOpenCallLogModal = () => {
    // Resetear formulario con valores por defecto
    setCallLogData({
      callDate: new Date().toISOString().split('T')[0],
      duration: '',
      notes: '',
      outcome: 'completed'
    });
    setCallLogModalOpen(true);
  };

  const handleSaveCallLog = async () => {
    if (!callLogData.notes.trim()) {
      toast.error('Por favor ingrese notas de la llamada');
      return;
    }

    try {
      setSavingCallLog(true);
      const token = localStorage.getItem('token');
      
      const activityData = {
        activity_type: 'call',
        activity_date: callLogData.callDate,
        description: callLogData.notes,
        duration_minutes: parseInt(callLogData.duration) || 0,
        outcome: callLogData.outcome,
        created_by: 'current_user' // Se puede mejorar con datos del usuario actual
      };
      
      await axios.post(
        `${API_URL}/api/clients-master/${clientId}/activity`,
        activityData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('✅ Llamada registrada exitosamente');
      setCallLogModalOpen(false);
      // Aquí se podría recargar el historial de actividades si existiera
    } catch (error) {
      console.error('Error saving call log:', error);
      toast.error(error.response?.data?.detail || 'Error al registrar llamada');
    } finally {
      setSavingCallLog(false);
    }
  };

  // ========================================
  // ACCIÓN 4: ELIMINAR CLIENTE
  // ========================================
  
  const handleOpenDeleteModal = () => {
    setDeletePassword('');
    setDeleteModalOpen(true);
  };

  const handleDeleteClient = async () => {
    if (!deletePassword) {
      toast.error('Por favor ingrese su contraseña para confirmar');
      return;
    }

    try {
      setDeleting(true);
      const token = localStorage.getItem('token');
      
      await axios.delete(
        `${API_URL}/api/clients-master/${clientId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          data: { password: deletePassword }
        }
      );
      
      toast.success('✅ Cliente eliminado exitosamente');
      setDeleteModalOpen(false);
      navigate('/clients');
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar cliente');
    } finally {
      setDeleting(false);
    }
  };

  // ========================================
  // FUNCIONES AUXILIARES
  // ========================================

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'En Vigor': { label: '✅ Vigente', className: 'bg-green-100 text-green-800 border-green-300' },
      'Vigente': { label: '✅ Vigente', className: 'bg-green-100 text-green-800 border-green-300' },
      'Activa': { label: '✅ Vigente', className: 'bg-green-100 text-green-800 border-green-300' },
      'Vencida': { label: '⚠️ Vencida', className: 'bg-red-100 text-red-800 border-red-300' },
      'Cancelada': { label: '❌ Cancelada', className: 'bg-gray-100 text-gray-800 border-gray-300' }
    };
    
    const config = statusMap[status] || { label: status, className: 'bg-gray-100 text-gray-800' };
    return <Badge className={`${config.className} border`}>{config.label}</Badge>;
  };

  const calculateAge = (dob) => {
    if (!dob) return null;
    try {
      const birthDate = new Date(dob);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      return age;
    } catch {
      return null;
    }
  };

  const handleUpdateActivity = async (activityId, newData) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_URL}/api/clients-master/activities/${activityId}`,
        newData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Actividad actualizada');
      // Recargar actividades
      const activitiesResponse = await axios.get(
        `${API_URL}/api/clients-master/${clientId}/activities`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setActivities(activitiesResponse.data.activities || []);
    } catch (error) {
      console.error('Error updating activity:', error);
      toast.error('Error al actualizar');
    }
  };

  const handleDeleteActivity = async (activityId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_URL}/api/clients-master/activities/${activityId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Actividad eliminada');
      setActivities(prev => prev.filter(a => a.id !== activityId));
    } catch (error) {
      console.error('Error deleting activity:', error);
      toast.error('Error al eliminar');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-slate-500">Cargando perfil del cliente...</div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-slate-500">Cliente no encontrado</div>
      </div>
    );
  }

  const age = calculateAge(client.dob);

  return (
    <div className="space-y-6 pb-8">
      {/* Botón Volver */}
      <Button
        onClick={() => navigate('/clients')}
        variant="outline"
        className="gap-2"
      >
        <ArrowLeft className="h-4 w-4" />
        Volver a Clientes
      </Button>

      {/* ========================================= */}
      {/* ENCABEZADO Y IDENTIFICACIÓN RÁPIDA */}
      {/* ========================================= */}
      <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-2 border-indigo-200">
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-4">
                <User className="h-10 w-10 text-indigo-600" />
                <div>
                  <h1 className="text-3xl font-bold text-slate-900">
                    {client.name}
                  </h1>
                  {client.alias && (
                    <p className="text-sm text-slate-600 italic">
                      Alias: "{client.alias}"
                    </p>
                  )}
                </div>
              </div>

              {/* Identificación Rápida */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 bg-white/50 rounded-lg p-4 border border-indigo-100">
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">RFC/DNI</p>
                  <p className="text-base font-medium text-slate-900">
                    {client.id_number || 'No especificado'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">Teléfono Principal</p>
                  <p className="text-base font-medium text-slate-900 flex items-center gap-2">
                    <Phone className="h-4 w-4 text-indigo-600" />
                    {client.phone || 'No especificado'}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase">Email</p>
                  <p className="text-base font-medium text-slate-900 flex items-center gap-2">
                    <Mail className="h-4 w-4 text-indigo-600" />
                    {client.email || 'No especificado'}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Acciones Prioritarias - AHORA FUNCIONALES */}
          <div className="flex flex-wrap gap-3 mt-6">
            <Button
              onClick={() => navigate(`/policies/new?clientId=${clientId}`)}
              className="gap-2 bg-green-600 hover:bg-green-700"
            >
              <Plus className="h-4 w-4" />
              Nueva Póliza
            </Button>
            <Button
              onClick={handleOpenEditModal}
              variant="outline"
              className="gap-2"
            >
              <Edit className="h-4 w-4" />
              Editar Información
            </Button>
            <Button
              onClick={handleSendEmail}
              variant="outline"
              className="gap-2"
            >
              <Mail className="h-4 w-4" />
              Enviar Email
            </Button>
            <Button
              onClick={handleOpenCallLogModal}
              variant="outline"
              className="gap-2"
            >
              <MessageCircle className="h-4 w-4" />
              Registrar Llamada
            </Button>
            <Button
              onClick={handleOpenDeleteModal}
              variant="outline"
              className="gap-2 text-red-600 hover:bg-red-50 border-red-300 ml-auto"
              size="sm"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ========================================= */}
      {/* MÉTRICAS DEL CLIENTE */}
      {/* ========================================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-2 border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-600">
              Total Pólizas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-blue-700">
                {metrics.totalPolicies}
              </div>
              <Shield className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-green-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-600">
              Pólizas Activas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-3xl font-bold text-green-700">
                {metrics.activePolicies}
              </div>
              <Shield className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-purple-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-600">
              Suma Asegurada Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-purple-700">
                {formatCurrency(metrics.totalInsuredAmount)}
              </div>
              <TrendingUp className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-2 border-orange-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold text-slate-600">
              Prima Anual Total
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="text-2xl font-bold text-orange-700">
                {formatCurrency(metrics.totalAnnualPremium)}
              </div>
              <CreditCard className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========================================= */}
      {/* PÓLIZAS VINCULADAS - TABLA COMPLETA */}
      {/* ========================================= */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-xl">
            <Shield className="h-6 w-6 text-indigo-600" />
            Pólizas Vinculadas ({policies.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {policies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Shield className="h-16 w-16 text-slate-300 mx-auto mb-4" />
              <p className="text-lg mb-4">No hay pólizas vinculadas a este cliente</p>
              <Button
                onClick={() => navigate(`/policies/new?clientId=${clientId}`)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Crear Primera Póliza
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b-2 border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Número
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Tipo
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Estado
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Vencimiento
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-slate-600 uppercase">
                      Monto Asegurado
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">
                      Objeto Asegurado
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {policies.map((policy) => (
                    <tr key={policy.id} className="hover:bg-slate-50">
                      <td className="px-4 py-4 text-sm font-medium text-slate-900">
                        {policy.policy_number}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {policy.policy_type || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {getStatusBadge(policy.status)}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {formatDate(policy.end_date)}
                      </td>
                      <td className="px-4 py-4 text-sm font-semibold text-right text-slate-900">
                        {formatCurrency(policy.insured_amount)}
                      </td>
                      <td className="px-4 py-4 text-sm text-slate-700">
                        {policy.vehicle?.make && policy.vehicle?.model
                          ? `${policy.vehicle.make} ${policy.vehicle.model} ${policy.vehicle.year || ''}`
                          : policy.main_insured?.name || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-center">
                        <Button
                          onClick={() => navigate(`/policies/${policy.id}`)}
                          variant="outline"
                          size="sm"
                          className="gap-2"
                        >
                          <Eye className="h-4 w-4" />
                          Ver Detalle
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Total de Primas */}
              <div className="mt-4 pt-4 border-t border-slate-200 flex justify-end">
                <div className="text-right">
                  <p className="text-sm text-slate-600">Total Prima Anual:</p>
                  <p className="text-2xl font-bold text-indigo-700">
                    {formatCurrency(metrics.totalAnnualPremium)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ========================================= */}
      {/* HISTORIAL DE ACTIVIDAD (FIX 2) */}
      {/* ========================================= */}
      <ClientActivityTimeline 
        activities={activities} 
        onUpdateActivity={handleUpdateActivity}
        onDeleteActivity={handleDeleteActivity}
      />

      {/* ========================================= */}
      {/* INFORMACIÓN PERSONAL */}
      {/* ========================================= */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-indigo-600" />
            Información Personal
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Dirección Principal */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-slate-700">
                <MapPin className="h-4 w-4 text-indigo-600" />
                Dirección Principal
              </div>
              <div className="pl-6 text-sm text-slate-600">
                {client.current_address?.street && (
                  <>
                    <p>{client.current_address.street}</p>
                    <p>
                      {client.current_address.city}, {client.current_address.state}
                    </p>
                    <p>
                      CP: {client.current_address.postal_code}, {client.current_address.country || 'México'}
                    </p>
                  </>
                )}
                {!client.current_address?.street && (
                  <p className="italic text-slate-400">No especificada</p>
                )}
              </div>
            </div>

            {/* Contacto */}
            <div className="space-y-3">
              <div className="text-sm font-semibold text-slate-700">Contacto</div>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-indigo-600" />
                  <span className="font-medium">Teléfono:</span> {client.phone || 'N/A'}
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-indigo-600" />
                  <span className="font-medium">Email:</span> {client.email || 'N/A'}
                </div>
              </div>
            </div>

            {/* Identificación */}
            <div className="space-y-3">
              <div className="text-sm font-semibold text-slate-700">Identificación</div>
              <div className="space-y-2 text-sm text-slate-600">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-indigo-600" />
                  <span className="font-medium">RFC/DNI:</span> {client.id_number || 'N/A'}
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-indigo-600" />
                  <span className="font-medium">Fecha de Nacimiento:</span>{' '}
                  {client.dob ? `${formatDate(client.dob)} ${age ? `(${age} años)` : ''}` : 'N/A'}
                </div>
              </div>
            </div>

            {/* Notas */}
            {client.notes && (
              <div className="space-y-3 md:col-span-2">
                <div className="text-sm font-semibold text-slate-700">Notas y Observaciones</div>
                <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
                  <p className="text-sm text-slate-700">{client.notes}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ========================================= */}
      {/* ETIQUETAS */}
      {/* ========================================= */}
      {client.tags && client.tags.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Etiquetas y Clasificación</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {client.tags.map((tag, index) => (
                <Badge key={index} className="bg-indigo-100 text-indigo-800 border-indigo-300">
                  {tag}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ========================================= */}
      {/* MODAL: EDITAR INFORMACIÓN */}
      {/* ========================================= */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Edit className="h-5 w-5 text-indigo-600" />
              Editar Información del Cliente
            </DialogTitle>
            <DialogDescription>
              Actualiza los datos del cliente. Los campos marcados con * son obligatorios.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Nombre */}
            <div>
              <Label htmlFor="edit-name">Nombre Completo *</Label>
              <Input
                id="edit-name"
                value={editFormData.name}
                onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                placeholder="Nombre completo del cliente"
              />
            </div>

            {/* Alias */}
            <div>
              <Label htmlFor="edit-alias">Alias</Label>
              <Input
                id="edit-alias"
                value={editFormData.alias}
                onChange={(e) => setEditFormData({...editFormData, alias: e.target.value})}
                placeholder="Apodo o nombre corto"
              />
            </div>

            {/* RFC/DNI */}
            <div>
              <Label htmlFor="edit-id">RFC/DNI</Label>
              <Input
                id="edit-id"
                value={editFormData.id_number}
                onChange={(e) => setEditFormData({...editFormData, id_number: e.target.value})}
                placeholder="RFC o identificación fiscal"
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={editFormData.email}
                onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                placeholder="correo@ejemplo.com"
              />
            </div>

            {/* Teléfono */}
            <div>
              <Label htmlFor="edit-phone">Teléfono</Label>
              <Input
                id="edit-phone"
                value={editFormData.phone}
                onChange={(e) => setEditFormData({...editFormData, phone: e.target.value})}
                placeholder="+52 (55) 1234-5678"
              />
            </div>

            {/* Fecha de Nacimiento */}
            <div>
              <Label htmlFor="edit-dob">Fecha de Nacimiento</Label>
              <Input
                id="edit-dob"
                type="date"
                value={editFormData.dob}
                onChange={(e) => setEditFormData({...editFormData, dob: e.target.value})}
              />
            </div>

            {/* Dirección */}
            <div className="border-t pt-4">
              <h4 className="font-semibold mb-3">Dirección</h4>
              
              <div className="space-y-3">
                <div>
                  <Label htmlFor="edit-street">Calle y Número</Label>
                  <Input
                    id="edit-street"
                    value={editFormData.current_address?.street || ''}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      current_address: {...editFormData.current_address, street: e.target.value}
                    })}
                    placeholder="Calle y número"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="edit-city">Ciudad</Label>
                    <Input
                      id="edit-city"
                      value={editFormData.current_address?.city || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        current_address: {...editFormData.current_address, city: e.target.value}
                      })}
                      placeholder="Ciudad"
                    />
                  </div>
                  <div>
                    <Label htmlFor="edit-state">Estado</Label>
                    <Input
                      id="edit-state"
                      value={editFormData.current_address?.state || ''}
                      onChange={(e) => setEditFormData({
                        ...editFormData,
                        current_address: {...editFormData.current_address, state: e.target.value}
                      })}
                      placeholder="Estado"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="edit-postal">Código Postal</Label>
                  <Input
                    id="edit-postal"
                    value={editFormData.current_address?.postal_code || ''}
                    onChange={(e) => setEditFormData({
                      ...editFormData,
                      current_address: {...editFormData.current_address, postal_code: e.target.value}
                    })}
                    placeholder="CP"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEditModalOpen(false)}
              disabled={saving}
            >
              <X className="h-4 w-4 mr-2" />
              Cancelar
            </Button>
            <Button
              onClick={handleSaveEdit}
              disabled={saving}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================= */}
      {/* MODAL: REGISTRAR LLAMADA */}
      {/* ========================================= */}
      <Dialog open={callLogModalOpen} onOpenChange={setCallLogModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-indigo-600" />
              Registrar Llamada
            </DialogTitle>
            <DialogDescription>
              Registra los detalles de tu llamada con {client.name}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Fecha de Llamada */}
            <div>
              <Label htmlFor="call-date">Fecha de la Llamada</Label>
              <Input
                id="call-date"
                type="date"
                value={callLogData.callDate}
                onChange={(e) => setCallLogData({...callLogData, callDate: e.target.value})}
              />
            </div>

            {/* Duración */}
            <div>
              <Label htmlFor="call-duration">Duración (minutos)</Label>
              <Input
                id="call-duration"
                type="number"
                value={callLogData.duration}
                onChange={(e) => setCallLogData({...callLogData, duration: e.target.value})}
                placeholder="15"
                min="0"
              />
            </div>

            {/* Resultado */}
            <div>
              <Label htmlFor="call-outcome">Resultado</Label>
              <select
                id="call-outcome"
                value={callLogData.outcome}
                onChange={(e) => setCallLogData({...callLogData, outcome: e.target.value})}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg"
              >
                <option value="completed">Llamada completada</option>
                <option value="no_answer">No contestó</option>
                <option value="voicemail">Buzón de voz</option>
                <option value="callback">Solicita llamar después</option>
              </select>
            </div>

            {/* Notas */}
            <div>
              <Label htmlFor="call-notes">Notas de la Llamada *</Label>
              <Textarea
                id="call-notes"
                value={callLogData.notes}
                onChange={(e) => setCallLogData({...callLogData, notes: e.target.value})}
                placeholder="Describe el contenido de la llamada, acuerdos, próximos pasos..."
                rows={5}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCallLogModalOpen(false)}
              disabled={savingCallLog}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSaveCallLog}
              disabled={savingCallLog}
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              {savingCallLog ? 'Guardando...' : 'Registrar Llamada'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================= */}
      {/* MODAL: ELIMINAR CLIENTE (ALTO RIESGO) */}
      {/* ========================================= */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2 text-red-600">
              <AlertCircle className="h-6 w-6" />
              ⚠️ Eliminar Cliente
            </DialogTitle>
          </DialogHeader>

          <Alert className="bg-red-50 border-red-300">
            <AlertCircle className="h-4 w-4 text-red-600" />
            <AlertDescription className="text-red-800">
              <strong>Esta acción es irreversible.</strong>
              <br />
              Se eliminará permanentemente a <strong>{client.name}</strong> y toda su información asociada.
            </AlertDescription>
          </Alert>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="delete-password" className="text-red-700">
                Ingrese su contraseña para confirmar
              </Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Tu contraseña"
                className="border-red-300 focus:border-red-500 focus:ring-red-500"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteModalOpen(false)}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteClient}
              disabled={deleting || !deletePassword}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting ? 'Eliminando...' : 'Confirmar Eliminación'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
